import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IGeo, IGeoApiProvider} from "data/providers/api/geo.api.provider";

export enum GeoCountryCode {
	US = "US",
}

export enum GeoCountryAlias {
	US = "US",
	USA = "USA",
	UnitedStates = "UNITED STATES",
	UnitedStatesOfAmerica = "UNITED STATES OF AMERICA",
}

export enum GeoBrand {
	Arrowhead = "Arrowhead",
	Deerpark = "Deerpark",
	IceMountain = "Ice Mountain",
	Ozarka = "Ozarka",
	PolandSprings = "Poland Springs",
	Zephyrhills = "Zephyrhills",
}

export enum GeoRegionCode {
	AL = "AL",
	AR = "AR",
	AZ = "AZ",
	CA = "CA",
	CO = "CO",
	CT = "CT",
	DC = "DC",
	DE = "DE",
	FL = "FL",
	GA = "GA",
	IA = "IA",
	ID = "ID",
	IL = "IL",
	IN = "IN",
	KS = "KS",
	KY = "KY",
	LA = "LA",
	MA = "MA",
	MD = "MD",
	ME = "ME",
	MI = "MI",
	MN = "MN",
	MO = "MO",
	MS = "MS",
	MT = "MT",
	NC = "NC",
	ND = "ND",
	NE = "NE",
	NH = "NH",
	NJ = "NJ",
	NM = "NM",
	NV = "NV",
	NY = "NY",
	OH = "OH",
	OK = "OK",
	OR = "OR",
	PA = "PA",
	RI = "RI",
	SC = "SC",
	SD = "SD",
	TN = "TN",
	TX = "TX",
	UT = "UT",
	VA = "VA",
	VT = "VT",
	WA = "WA",
	WI = "WI",
	WV = "WV",
	WY = "WY",
}

export interface IGeoRegion {
	readonly name: string;
	readonly code: GeoRegionCode;
}

export interface IGeoBrandConfig {
	readonly countryCode: GeoCountryCode;
	readonly regions: readonly IGeoRegion[];
}

export type GeoLocation = Pick<IGeo, "brand" | "country" | "state">;
export type GeoBrandValue = string | null | undefined;
export type GeoStateValue = string | GeoRegionCode | null | undefined;
export type GeoCountryValue = string | GeoCountryCode | null | undefined;
export type GeoConfig = Readonly<Record<GeoBrand, IGeoBrandConfig>>;

const GEO_BRANDS: readonly GeoBrand[] = [
	GeoBrand.Arrowhead,
	GeoBrand.Deerpark,
	GeoBrand.IceMountain,
	GeoBrand.Ozarka,
	GeoBrand.PolandSprings,
	GeoBrand.Zephyrhills,
];

const GEO_CONFIG: GeoConfig = {
	[GeoBrand.Arrowhead]: {
		countryCode: GeoCountryCode.US,
		regions: [
			{name: "Arizona", code: GeoRegionCode.AZ},
			{name: "California", code: GeoRegionCode.CA},
			{name: "Nevada", code: GeoRegionCode.NV},
			{name: "Oregon", code: GeoRegionCode.OR},
			{name: "Washington State", code: GeoRegionCode.WA},
			{name: "Idaho", code: GeoRegionCode.ID},
			{name: "Montana", code: GeoRegionCode.MT},
			{name: "Wyoming", code: GeoRegionCode.WY},
			{name: "Utah", code: GeoRegionCode.UT},
			{name: "Colorado", code: GeoRegionCode.CO},
		],
	},
	[GeoBrand.Deerpark]: {
		countryCode: GeoCountryCode.US,
		regions: [
			{name: "Pennsylvania", code: GeoRegionCode.PA},
			{name: "West Virginia", code: GeoRegionCode.WV},
			{name: "Virginia", code: GeoRegionCode.VA},
			{name: "Delaware", code: GeoRegionCode.DE},
			{name: "Maryland", code: GeoRegionCode.MD},
			{name: "Kentucky", code: GeoRegionCode.KY},
			{name: "Tennessee", code: GeoRegionCode.TN},
			{name: "North Carolina", code: GeoRegionCode.NC},
			{name: "South Carolina", code: GeoRegionCode.SC},
			{name: "Georgia", code: GeoRegionCode.GA},
			{name: "Alabama", code: GeoRegionCode.AL},
		],
	},
	[GeoBrand.IceMountain]: {
		countryCode: GeoCountryCode.US,
		regions: [
			{name: "Missouri", code: GeoRegionCode.MO},
			{name: "North Dakota", code: GeoRegionCode.ND},
			{name: "South Dakota", code: GeoRegionCode.SD},
			{name: "Nebraska", code: GeoRegionCode.NE},
			{name: "Iowa", code: GeoRegionCode.IA},
			{name: "Minnesota", code: GeoRegionCode.MN},
			{name: "Wisconsin", code: GeoRegionCode.WI},
			{name: "Michigan", code: GeoRegionCode.MI},
			{name: "Illinois", code: GeoRegionCode.IL},
			{name: "Ohio", code: GeoRegionCode.OH},
			{name: "Indiana", code: GeoRegionCode.IN},
		],
	},
	[GeoBrand.Ozarka]: {
		countryCode: GeoCountryCode.US,
		regions: [
			{name: "Texas", code: GeoRegionCode.TX},
			{name: "Louisiana", code: GeoRegionCode.LA},
			{name: "Oklahoma", code: GeoRegionCode.OK},
			{name: "New Mexico", code: GeoRegionCode.NM},
			{name: "Mississippi", code: GeoRegionCode.MS},
			{name: "Kansas", code: GeoRegionCode.KS},
			{name: "Arkansas", code: GeoRegionCode.AR},
		],
	},
	[GeoBrand.PolandSprings]: {
		countryCode: GeoCountryCode.US,
		regions: [
			{name: "Maine", code: GeoRegionCode.ME},
			{name: "New Hampshire", code: GeoRegionCode.NH},
			{name: "Vermont", code: GeoRegionCode.VT},
			{name: "Rhode Island", code: GeoRegionCode.RI},
			{name: "Massachusetts", code: GeoRegionCode.MA},
			{name: "Connecticut", code: GeoRegionCode.CT},
			{name: "New Jersey", code: GeoRegionCode.NJ},
			{name: "New York", code: GeoRegionCode.NY},
			{name: "Washington DC", code: GeoRegionCode.DC},
		],
	},
	[GeoBrand.Zephyrhills]: {
		countryCode: GeoCountryCode.US,
		regions: [{name: "Florida", code: GeoRegionCode.FL}],
	},
};

const GEO_BRAND_LOOKUP: Readonly<Record<string, GeoBrand>> = {
	ARROWHEAD: GeoBrand.Arrowhead,
	DEERPARK: GeoBrand.Deerpark,
	"ICE MOUNTAIN": GeoBrand.IceMountain,
	OZARKA: GeoBrand.Ozarka,
	"POLAND SPRINGS": GeoBrand.PolandSprings,
	ZEPHYRHILLS: GeoBrand.Zephyrhills,
};

export interface IGeoStore {
	get geo(): IGeo | null;
	get config(): GeoConfig;
	get brands(): readonly GeoBrand[];
	get currentBrand(): GeoBrand | null;

	fetchGeo(): Promise<void>;
	getBrandConfig(brand: GeoBrand): IGeoBrandConfig;
	getRegionsByBrand(brand: GeoBrand): readonly IGeoRegion[];
	getBrandByGeo(geo?: GeoLocation | null): GeoBrand | null;
	getBrandByName(brand: GeoBrandValue): GeoBrand | null;
	getBrandByState(state: GeoStateValue, country?: GeoCountryValue): GeoBrand | null;
	isGeoSupportedByBrand(brand: GeoBrand, geo?: GeoLocation | null): boolean;
	isStateSupportedByBrand(
		brand: GeoBrand,
		state: GeoStateValue,
		country?: GeoCountryValue
	): boolean;
}

@injectable()
export class GeoStore implements IGeoStore {
	@observable private _geo: IGeo | null = null;

	get geo() {
		return this._geo;
	}

	get config() {
		return GEO_CONFIG;
	}

	get brands() {
		return GEO_BRANDS;
	}

	get currentBrand() {
		return this.getBrandByGeo(this._geo);
	}

	constructor(@inject(Bindings.GeoApiProvider) private _geoApiProvider: IGeoApiProvider) {
		makeAutoObservable(this);
	}

	@action
	async fetchGeo() {
		const {data} = await this._geoApiProvider.geo();

		console.log(data);
		runInAction(() => {
			this._geo = data.success.geo;
		});
	}

	getBrandConfig(brand: GeoBrand) {
		return GEO_CONFIG[brand];
	}

	getRegionsByBrand(brand: GeoBrand) {
		return this.getBrandConfig(brand).regions;
	}

	getBrandByGeo(geo: GeoLocation | null = this._geo) {
		if (!geo) {
			return null;
		}

		return this.getBrandByName(geo.brand) ?? this.getBrandByState(geo.state, geo.country);
	}

	isGeoSupportedByBrand(brand: GeoBrand, geo: GeoLocation | null = this._geo) {
		if (!geo) {
			return false;
		}

		const resolvedBrand = this.getBrandByName(geo.brand);

		if (resolvedBrand) {
			return resolvedBrand === brand;
		}

		return this.isStateSupportedByBrand(brand, geo.state, geo.country);
	}

	getBrandByName(brand: GeoBrandValue) {
		const normalizedBrand = this._normalizeText(brand);

		return GEO_BRAND_LOOKUP[normalizedBrand] ?? null;
	}

	getBrandByState(state: GeoStateValue, country?: GeoCountryValue) {
		const normalizedState = this._normalizeText(state);

		if (!normalizedState) {
			return null;
		}

		return (
			GEO_BRANDS.find((brand) =>
				this.isStateSupportedByBrand(brand, normalizedState, country)
			) ?? null
		);
	}

	isStateSupportedByBrand(brand: GeoBrand, state: GeoStateValue, country?: GeoCountryValue) {
		const normalizedState = this._normalizeText(state);
		const normalizedCountry = this._normalizeCountry(country);
		const config = this.getBrandConfig(brand);

		if (!normalizedState) {
			return false;
		}

		return (
			(!normalizedCountry || config.countryCode === normalizedCountry) &&
			config.regions.some(
				({code, name}) =>
					code === normalizedState || this._normalizeText(name) === normalizedState
			)
		);
	}

	private _normalizeCountry(value: GeoCountryValue) {
		const normalizedValue = this._normalizeText(value);

		switch (normalizedValue) {
			case GeoCountryAlias.US:
			case GeoCountryAlias.USA:
			case GeoCountryAlias.UnitedStates:
			case GeoCountryAlias.UnitedStatesOfAmerica:
				return GeoCountryCode.US;
			default:
				return normalizedValue;
		}
	}

	private _normalizeText(value: string | null | undefined) {
		return value?.trim().toUpperCase() ?? "";
	}
}
